"use client";

import { useState } from "react";
import {
  Field,
  TextInput,
  CheckboxRow,
  PrintButton,
  PrintFooter,
  trackToolResult,
} from "@/components/tools/ui";

/* Builds a three-part health & safety policy — Statement of Intent, Organisation,
   Arrangements — the structure the HSE expects. A written policy is a legal duty
   under the Health and Safety at Work etc. Act 1974 s.2(3) once an employer has five
   or more employees.

   Compliance (YMYL): drafts a policy from standard arrangements; the employer must
   review it, make it specific to their business and risks, sign it and keep it up to
   date. Not a substitute for proper arrangements actually being in place. */

interface Arr { id: string; topic: string; text: string }
const ARRANGEMENTS: Arr[] = [
  { id: "ra", topic: "Risk assessments", text: "Suitable and sufficient risk assessments are carried out for work activities, recorded where 5+ employees, and reviewed when things change.", },
  { id: "ssow", topic: "Safe systems of work (RAMS)", text: "Method statements and safe systems of work are prepared for higher-risk tasks and briefed to those carrying out the work.", },
  { id: "training", topic: "Information, instruction & training", text: "Employees receive the information, instruction and training needed to work safely, with records kept.", },
  { id: "firstaid", topic: "First aid", text: "First-aid provision is based on a first-aid needs assessment, with trained first-aiders and stocked kits.", },
  { id: "accidents", topic: "Accidents & RIDDOR", text: "Accidents, near misses and ill health are recorded and investigated, and reportable events are notified to the HSE under RIDDOR.", },
  { id: "fire", topic: "Fire & emergency", text: "A fire risk assessment is maintained, with detection, escape routes, extinguishers, an emergency plan and drills.", },
  { id: "coshh", topic: "Hazardous substances (COSHH)", text: "Substances hazardous to health are assessed under COSHH, with controls and safety data sheets available.", },
  { id: "manual", topic: "Manual handling", text: "Manual handling is avoided where reasonably practicable and assessed where it cannot be, following the hierarchy of control.", },
  { id: "ppe", topic: "PPE", text: "Personal protective equipment is provided free of charge where risks cannot be controlled by other means, and is maintained.", },
  { id: "puwer", topic: "Work equipment (PUWER)", text: "Work equipment is suitable, maintained and inspected, and used only by trained, authorised people.", },
  { id: "dse", topic: "Display screen equipment", text: "DSE users have workstation assessments and are entitled to eye tests, under the DSE Regulations.", },
  { id: "noise", topic: "Noise & vibration", text: "Exposure to noise and hand-arm vibration is assessed and controlled against the action and limit values.", },
  { id: "contractors", topic: "Contractors & visitors", text: "Contractors are selected for competence, given site rules and coordinated; visitors are signed in and kept safe.", },
  { id: "consult", topic: "Consultation", text: "Employees are consulted on matters affecting their health and safety.", },
  { id: "review", topic: "Monitoring & review", text: "Health and safety performance is monitored, and this policy is reviewed at least annually and after significant change.", },
];
const DEFAULT_ON = new Set(["ra", "ssow", "training", "firstaid", "accidents", "fire", "coshh", "manual", "ppe", "puwer", "consult", "review"]);

export function HealthSafetyPolicy() {
  const [company, setCompany] = useState("");
  const [senior, setSenior] = useState("");
  const [seniorRole, setSeniorRole] = useState("Director");
  const [competent, setCompetent] = useState("");
  const [date, setDate] = useState("");
  const [on, setOn] = useState<Record<string, boolean>>(() => Object.fromEntries(ARRANGEMENTS.map((a) => [a.id, DEFAULT_ON.has(a.id)])));

  const chosen = ARRANGEMENTS.filter((a) => on[a.id]);
  const ready = !!company && !!senior;
  if (ready) trackToolResult("health-safety-policy", { arrangements: chosen.length });

  return (
    <div className="space-y-8">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Company name"><TextInput value={company} onChange={(e) => setCompany(e.target.value)} placeholder="e.g. Acme Build Ltd" /></Field>
        <Field label="Signed by (most senior person)"><TextInput value={senior} onChange={(e) => setSenior(e.target.value)} placeholder="e.g. J. Smith" /></Field>
        <Field label="Their role"><TextInput value={seniorRole} onChange={(e) => setSeniorRole(e.target.value)} placeholder="e.g. Managing Director" /></Field>
        <Field label="Day-to-day H&S responsibility"><TextInput value={competent} onChange={(e) => setCompetent(e.target.value)} placeholder="Competent person / manager" /></Field>
        <Field label="Date"><TextInput value={date} onChange={(e) => setDate(e.target.value)} placeholder="e.g. June 2026" /></Field>
      </div>

      <fieldset className="rounded-2xl border border-line bg-white p-5 shadow-soft">
        <legend className="px-2 font-display text-base font-bold text-ink">Arrangements to include</legend>
        <p className="mt-1 px-2 text-xs text-muted">Tick the topics relevant to your business — they become Part 3 of the policy.</p>
        <div className="mt-3 grid gap-2.5 sm:grid-cols-2">
          {ARRANGEMENTS.map((a) => <CheckboxRow key={a.id} checked={!!on[a.id]} onChange={(v) => setOn((s) => ({ ...s, [a.id]: v }))}>{a.topic}</CheckboxRow>)}
        </div>
      </fieldset>

      {/* Printable policy */}
      {ready && (
        <div className="rounded-2xl border border-line bg-white p-6 shadow-soft">
          <h3 className="font-display text-lg font-bold text-ink">Health &amp; Safety Policy — {company}</h3>

          <h4 className="mt-5 font-display text-base font-bold text-ink">Part 1 — Statement of Intent</h4>
          <p className="mt-2 text-sm leading-relaxed text-body">
            {company} is committed to ensuring, so far as is reasonably practicable, the health, safety
            and welfare at work of all its employees, and the health and safety of others who may be
            affected by its activities. We will provide and maintain safe systems of work, safe
            equipment, information, instruction, training and supervision, and we will assess and
            control risks. This policy will be reviewed regularly and brought to the attention of all
            employees. Overall responsibility for health and safety rests with {senior},{" "}
            {seniorRole}.
          </p>
          <p className="mt-3 text-sm text-body">Signed: {senior} ({seniorRole}){date && ` · ${date}`}</p>

          <h4 className="mt-5 font-display text-base font-bold text-ink">Part 2 — Organisation</h4>
          <ul className="mt-2 space-y-1.5 text-sm text-body">
            <li><strong>{senior} ({seniorRole})</strong> holds overall and final responsibility for health and safety.</li>
            <li><strong>{competent || "The appointed competent person"}</strong> is responsible for day-to-day implementation, advice and monitoring.</li>
            <li><strong>All employees</strong> must take reasonable care of themselves and others, follow training and procedures, use equipment and PPE correctly, and report hazards, accidents and near misses.</li>
          </ul>

          <h4 className="mt-5 font-display text-base font-bold text-ink">Part 3 — Arrangements</h4>
          <ul className="mt-2 space-y-2 text-sm text-body">
            {chosen.map((a) => (
              <li key={a.id}><strong>{a.topic}.</strong> {a.text}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <PrintButton tool="health-safety-policy" label="Print / save this policy" />
        {!ready && <span className="text-xs text-muted">Enter the company name and signatory to generate the policy.</span>}
      </div>
      <PrintFooter toolName="Health & Safety Policy" />
    </div>
  );
}
